// Platform answers, in one place. Three different questions get asked, and
// each has its own answer:
//   - Which app is running? IS_MOBILE_APP. Behavior and layout follow this:
//     hover reveal, the keyboard toolbar, the panel as a drawer, settings.
//   - Is this a phone or tablet operating system? IS_MOBILE_OS. Things the
//     OS decides follow this: no self-updater, no hardware keyboard to
//     advertise shortcuts for.
//   - Which modifier key do shortcuts use? IS_MAC (⌘ rather than Ctrl).
//   - How wide is the window right now? Ask the window (or a CSS media
//     query), and only for appearance, never behavior.
// tests/platformChecks.test.ts keeps device sniffing out of everywhere else.

const userAgent = typeof navigator !== 'undefined' ? navigator.userAgent : ''

/** A phone or tablet operating system (Android, iOS, iPadOS). */
export const IS_MOBILE_OS = /Android|iPhone|iPad|iPod/i.test(userAgent)

/** Android in particular, for its system bars and other Android-only quirks. */
export const IS_ANDROID = /Android/i.test(userAgent)

/** macOS, where shortcuts use ⌘ for Mod (iPads with a keyboard report this too). */
export const IS_MAC = /Macintosh|Mac OS X/i.test(userAgent)

/**
 * Which app is running: the phone app (AppMobile) or the desktop app (App).
 * Phones and tablets get the phone app, and so does a browser window too
 * narrow for the desktop layout. Settings follow this: each app shows only
 * its own, and per-device values (text size, width, typewriter mode) are read
 * and written for the app in hand.
 */
export const IS_MOBILE_APP =
  IS_MOBILE_OS || (typeof window !== 'undefined' && window.innerWidth < 768)

export type AppPlatform = 'desktop' | 'mobile'

const APP_PLATFORM: AppPlatform = IS_MOBILE_APP ? 'mobile' : 'desktop'

/** Whether something tagged for a platform belongs in the running app. Untagged belongs everywhere. */
export function onThisPlatform(platform?: AppPlatform): boolean {
  return !platform || platform === APP_PLATFORM
}

// Lets stylesheets follow the app in hand rather than guess from media queries.
if (typeof document !== 'undefined') {
  document.documentElement.classList.add(IS_MOBILE_APP ? 'app-mobile' : 'app-desktop')
}
